import { Mapper } from '@gleamer/types';
import { DicomMetadataStore } from '@ohif/core';
import { atom, getDefaultStore } from 'jotai';
import {
  activeDetectionAtom,
  displayPredictionsAtom,
  predictionsConfidencesAtom,
  resetPredictionsAtom,
  setPredictionsAtom,
} from '../contexts/predictions.atoms';
import { bacResultToPredictions, DroppedView } from './bacResultToPredictions';
import { FramedInstance, getFrameGeometry } from './getFrameGeometry';

/** True once a JSON dropped in `/local-bac` was read; gates the bac mode's panel. */
export const preAnnotationsReadAtom = atom(false);

export function hasReadPreAnnotations(): boolean {
  return getDefaultStore().get(preAnnotationsReadAtom);
}

type StoredInstance = FramedInstance & {
  StudyInstanceUID: string;
  SeriesInstanceUID: string;
  SOPInstanceUID: string;
};

/** The dropped DICOM instances, by SOPInstanceUID. */
function getDroppedViews(): Map<string, DroppedView> {
  const views = new Map<string, DroppedView>();
  DicomMetadataStore.getStudyInstanceUIDs().forEach((studyInstanceUID) => {
    DicomMetadataStore.getStudy(studyInstanceUID)?.series.forEach((series) => {
      series.instances.forEach((instance: StoredInstance) => {
        views.set(instance.SOPInstanceUID, {
          studyInstanceUID: instance.StudyInstanceUID,
          seriesInstanceUID: instance.SeriesInstanceUID,
          sopInstanceUID: instance.SOPInstanceUID,
          numberOfFrames: Number(instance.NumberOfFrames) || 1,
          getFrameGeometry: (frameNumber) =>
            getFrameGeometry(instance, frameNumber),
        });
      });
    });
  });
  return views;
}

/** A study as the Predictions panel lists it. */
export type ListedStudy = {
  StudyInstanceUID: string;
  StudyDate: string;
  series: { SeriesInstanceUID: string; SeriesNumber: number }[];
};

/**
 * The dropped studies, in the shape the Predictions panel lists. Outside a
 * task there is no current item to read them from.
 */
export function getDroppedStudies(): ListedStudy[] {
  return DicomMetadataStore.getStudyInstanceUIDs().flatMap(
    (studyInstanceUID) => {
      const study = DicomMetadataStore.getStudy(studyInstanceUID);
      if (!study) {
        return [];
      }
      return [
        {
          StudyInstanceUID: studyInstanceUID,
          StudyDate: study.series[0]?.instances[0]?.StudyDate ?? '',
          series: study.series.map((series) => ({
            SeriesInstanceUID: series.SeriesInstanceUID,
            SeriesNumber: series.instances[0]?.SeriesNumber,
          })),
        },
      ];
    }
  );
}

async function readJson(file: File): Promise<unknown> {
  try {
    return JSON.parse(await file.text());
  } catch (error) {
    console.warn(`[pre-annotations] ${file.name} is not valid JSON`, error);
    return undefined;
  }
}

/**
 * Keeps the Pre-annotations of the AI team's JSON files dropped in `/local-bac`,
 * for the bac mode `/local-bac` opens next. Runs once the dropped DICOMs are in the
 * DicomMetadataStore, since DBT Pre-annotations need their frames' planes.
 * Nothing leaves the browser.
 */
export async function keepDroppedPreAnnotations(
  jsonFiles: File[]
): Promise<void> {
  const store = getDefaultStore();
  store.set(resetPredictionsAtom);
  store.set(preAnnotationsReadAtom, false);

  const results = (await Promise.all(jsonFiles.map(readJson))).filter(
    (result) => result !== undefined
  );
  if (!results.length) {
    return;
  }

  const views = getDroppedViews();
  const predictions = await Promise.all(
    results
      .flatMap((result) =>
        bacResultToPredictions(result, (sopInstanceUID) =>
          views.get(sopInstanceUID)
        )
      )
      .map((prediction) =>
        Mapper.PredictionMapper.toPredictionInternalWithDisplaySet(
          prediction,
          prediction.seriesInstanceUID
        )
      )
  );

  store.set(setPredictionsAtom, {
    predictions,
    overrideInitialPredictions: true,
  });
  // Every Pre-annotation counts whatever its score: show the whole range.
  store.set(predictionsConfidencesAtom, [0, 100]);
  // setPredictionsAtom selects the first detection; nothing is selected until
  // the annotator clicks one in the panel.
  store.set(activeDetectionAtom, '');
  store.set(displayPredictionsAtom, true);
  store.set(preAnnotationsReadAtom, true);
}
